import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Responsive, useContainerWidth } from 'react-grid-layout';
import { ACCOUNT_WIDGET_META, DASHBOARD_BREAKPOINTS, DASHBOARD_BREAKPOINT_KEYS, DASHBOARD_COLS, DASHBOARD_MARGIN, DASHBOARD_ROW_HEIGHT, nextWidgetId, normalizeAccountDashboard, widgetMeta, widgetsForHost, } from '@techstack/core';
import { WidgetShell } from './WidgetShell.js';
import { WidgetPreview } from './WidgetPreview.js';
import { rungForCell } from './rungs.js';
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
export function AccountBoard({ host, config, onChange, registry, configEditors, onOpenWidget, canOpenWidget, openHintFor, onReset, catalogFilter, saveState = 'idle', onRetrySave, toolbarExtra, toolbarInto, className, }) {
    const [editing, setEditing] = useState(false);
    const [catalogOpen, setCatalogOpen] = useState(false);
    const [configuring, setConfiguring] = useState(null);
    const { width, containerRef, mounted } = useContainerWidth();
    // Current breakpoint, derived from the measured width using the same cutoffs
    // the grid uses, so keyboard moves act on the layout the user can see.
    const bp = width >= DASHBOARD_BREAKPOINTS.lg ? 'lg' : width >= DASHBOARD_BREAKPOINTS.md ? 'md' : 'sm';
    const catalog = useMemo(() => {
        const groups = new Map();
        for (const meta of widgetsForHost(host)) {
            if (catalogFilter && !catalogFilter(meta))
                continue;
            const list = groups.get(meta.group) || [];
            list.push(meta);
            groups.set(meta.group, list);
        }
        return Array.from(groups.entries());
    }, [host, catalogFilter]);
    const placedTypes = useMemo(() => new Set(config.widgets.map((w) => w.type)), [config.widgets]);
    // --- mutations -------------------------------------------------------------
    // Every mutation goes through the parent. `onChange` is the ONLY write path,
    // so a host can never end up rendering an arrangement it didn't persist.
    //
    // Mutations build on this ref, never on the `config` a handler closed over.
    // Adding a widget makes RGL re-measure and fire onLayoutChange in the same
    // tick, and a handler still holding the pre-add config would spread it back
    // over the new one — the widget appeared and vanished. Reading the newest
    // config at call time is what makes concurrent handlers compose instead of
    // clobber.
    const configRef = useRef(config);
    configRef.current = config;
    const commit = useCallback((next) => {
        configRef.current = next;
        onChange(next);
    }, [onChange]);
    /**
     * RGL fires onLayoutChange for plain width/breakpoint recomputation too, not
     * only for user edits. Persisting those would let merely opening the page in
     * a narrow window rewrite the saved board — so view mode is read-only and
     * only edit-mode changes are committed.
     */
    const onLayoutChange = useCallback((_current, all) => {
        if (!editing)
            return;
        const base = configRef.current;
        const ids = new Set(base.widgets.map((w) => w.id));
        const next = {};
        for (const b of DASHBOARD_BREAKPOINT_KEYS) {
            const incoming = (all[b] || []).filter((it) => ids.has(it.i));
            // RGL reports only the breakpoint it is currently rendering, and only
            // the items it has measured. Anything it didn't mention keeps the
            // placement we already had — otherwise resizing on a laptop would wipe
            // the phone layout, and a just-added widget would lose its slot before
            // RGL first measured it.
            const seen = new Set(incoming.map((it) => it.i));
            const kept = (base.layouts[b] || []).filter((it) => ids.has(it.i) && !seen.has(it.i));
            next[b] = [...incoming, ...kept];
        }
        commit({ ...base, layouts: next });
    }, [editing, commit]);
    const addWidget = useCallback((type) => {
        const config = configRef.current;
        const id = nextWidgetId(config.widgets, type);
        const meta = widgetMeta(type);
        const size = meta ? meta.defaultSize : { w: 6, h: 8 };
        const layouts = {};
        for (const b of DASHBOARD_BREAKPOINT_KEYS) {
            const cols = DASHBOARD_COLS[b];
            const items = [...(config.layouts[b] || [])];
            // New widgets land at the bottom, full width on the narrow grid. Any
            // other placement means a widget you just added appears somewhere you
            // then have to hunt for.
            const bottom = items.reduce((m, it) => Math.max(m, it.y + it.h), 0);
            items.push({
                i: id,
                x: 0,
                y: bottom,
                w: b === 'sm' ? cols : Math.min(size.w, cols),
                h: size.h,
            });
            layouts[b] = items;
        }
        commit({ ...config, widgets: [...config.widgets, { id, type }], layouts });
        setCatalogOpen(false);
    }, [commit]);
    const removeWidget = useCallback((id) => {
        setConfiguring((c) => (c === id ? null : c));
        const config = configRef.current;
        const layouts = {};
        for (const b of DASHBOARD_BREAKPOINT_KEYS) {
            layouts[b] = (config.layouts[b] || []).filter((it) => it.i !== id);
        }
        commit({ ...config, widgets: config.widgets.filter((w) => w.id !== id), layouts });
    }, [commit]);
    const updateWidgetConfig = useCallback((id, cfg) => {
        const config = configRef.current;
        commit({
            ...config,
            widgets: config.widgets.map((w) => (w.id === id ? { ...w, config: cfg } : w)),
        });
    }, [commit]);
    // Arrows move one cell, shift+arrows resize one cell. Acts on the CURRENT
    // breakpoint only; RGL's compactor then settles the result, so an up-move
    // inside an already-compacted column can snap back — the same physics a
    // mouse drag obeys.
    const keyMove = useCallback((id, dx, dy, dw, dh) => {
        const config = configRef.current;
        const widget = config.widgets.find((w) => w.id === id);
        const meta = widget ? widgetMeta(widget.type) : null;
        const min = meta ? meta.minSize : { w: 3, h: 4 };
        const cols = DASHBOARD_COLS[bp];
        const items = (config.layouts[bp] || []).map((it) => {
            if (it.i !== id)
                return it;
            const w = clamp(it.w + dw, Math.min(min.w, cols), cols);
            return {
                ...it,
                w,
                h: Math.max(min.h, it.h + dh),
                x: clamp(it.x + dx, 0, cols - w),
                y: Math.max(0, it.y + dy),
            };
        });
        commit({ ...config, layouts: { ...config.layouts, [bp]: items } });
    }, [bp, commit]);
    // Min sizes are re-attached from the catalog at render time rather than
    // stored in the blob: a widget whose minimum changes in a later build must
    // take effect for boards saved before that change.
    const rglLayouts = useMemo(() => {
        const out = {};
        const minById = new Map(config.widgets.map((w) => {
            const meta = widgetMeta(w.type);
            return [w.id, meta ? meta.minSize : { w: 3, h: 4 }];
        }));
        for (const b of DASHBOARD_BREAKPOINT_KEYS) {
            const cols = DASHBOARD_COLS[b];
            out[b] = (config.layouts[b] || []).map((it) => {
                const min = minById.get(it.i) || { w: 3, h: 4 };
                return { ...it, minW: Math.min(min.w, cols), minH: min.h };
            });
        }
        return out;
    }, [config]);
    const dragConfig = useMemo(() => ({ enabled: editing, handle: '.abw__grip' }), [editing]);
    const resizeConfig = useMemo(() => ({ enabled: editing }), [editing]);
    // react-grid-layout ships React 19 typings, where a RefObject's `current` is
    // nullable; the @types/react@18 this package builds against still wants the
    // non-null form. Same object at runtime — the cast closes a typings-version
    // gap, it does not claim the ref is always attached.
    const gridRef = containerRef;
    const toggleEditing = useCallback(() => {
        setEditing((v) => !v);
        setCatalogOpen(false);
        setConfiguring(null);
    }, []);
    /*
     * The toolbar (Customize / Done, Add widget, Reset, save state) is its own
     * 32px band by default. A host that has a row with room to spare — the
     * account page's tab strip — can hand over a slot and the toolbar renders
     * there instead, wrapped in `.ab` so the board's tokens and button styles
     * follow it out of the tree. Optional and null-safe: GAS passes nothing.
     */
    const toolbar = (_jsxs("div", { className: "ab__toolbar", children: [toolbarExtra, _jsxs("div", { className: "ab__toolbar-right", children: [_jsx(SaveIndicator, { state: saveState, onRetry: onRetrySave }), editing && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "ab__catalog-wrap", children: [_jsx("button", { type: "button", className: "ab__btn", "aria-expanded": catalogOpen, onClick: () => setCatalogOpen((v) => !v), children: "+ Add widget" }), catalogOpen && (_jsxs(_Fragment, { children: [_jsx("div", { className: "ab__catalog-scrim", onClick: () => setCatalogOpen(false) }), _jsx("div", { className: "ab__catalog", role: "menu", children: catalog.map(([group, metas]) => (_jsxs("div", { className: "ab__catalog-group", children: [_jsx("div", { className: "ab__catalog-group-label", children: group }), metas.map((meta) => (_jsxs("button", { type: "button", role: "menuitem", className: "ab__catalog-item", onClick: () => addWidget(meta.type), children: [_jsx(WidgetPreview, { type: meta.type, className: "ab__catalog-thumb" }), _jsxs("span", { className: "ab__catalog-item-text", children: [_jsxs("span", { className: "ab__catalog-item-title", children: [meta.title, placedTypes.has(meta.type) && (_jsx("span", { className: "ab__catalog-added", title: "Already on the board", children: "on board" }))] }), _jsx("span", { className: "ab__catalog-item-blurb", children: meta.blurb })] })] }, meta.type)))] }, group))) })] }))] }), onReset && (_jsx("button", { type: "button", className: "ab__btn ab__btn--quiet", onClick: onReset, children: "Reset" }))] })), _jsx("button", { type: "button", className: `ab__btn${editing ? ' ab__btn--primary' : ''}`, onClick: toggleEditing, children: editing ? 'Done' : 'Customize' })] })] }));
    return (_jsxs("div", { className: `ab${editing ? ' ab--editing' : ''}${className ? ` ${className}` : ''}`, children: [toolbarInto ? createPortal(_jsx("div", { className: "ab ab--toolbar-only", children: toolbar }), toolbarInto) : toolbar, editing && (_jsx("p", { className: "ab__hint", children: "Drag by the handle to move, pull the bottom-right corner to resize, \u00D7 to remove. Arrow keys move the focused widget; shift+arrows resize it." })), config.widgets.length === 0 ? (_jsxs("div", { className: "ab__empty", children: ["Nothing on this board yet \u2014 ", _jsx("strong", { children: "Customize \u2192 Add widget" }), " to build it."] })) : (_jsx("div", { ref: gridRef, className: "ab__grid-wrap", children: mounted && (_jsx(Responsive, { className: "ab__grid", width: width, breakpoints: DASHBOARD_BREAKPOINTS, cols: DASHBOARD_COLS, rowHeight: DASHBOARD_ROW_HEIGHT, margin: DASHBOARD_MARGIN, containerPadding: [0, 0], layouts: rglLayouts, dragConfig: dragConfig, resizeConfig: resizeConfig, onLayoutChange: onLayoutChange, children: config.widgets.map((widget) => {
                        const meta = widgetMeta(widget.type);
                        const Body = registry[widget.type];
                        // The rung comes from the layout the board already holds, not
                        // from measuring the rendered cell: the arithmetic is exact,
                        // costs nothing, and avoids every tile flashing its standard
                        // form for one frame before an observer corrects it.
                        const placed = (config.layouts[bp] || []).find((it) => it.i === widget.id);
                        const rung = placed
                            ? rungForCell({ w: placed.w, h: placed.h, bp, containerWidth: width })
                            : 'standard';
                        const Editor = configEditors ? configEditors[widget.type] : undefined;
                        const hasTarget = !!meta && !!(meta.deepHref || meta.deepTab);
                        const canOpen = hasTarget && (!canOpenWidget || canOpenWidget(meta));
                        const open = meta && onOpenWidget && canOpen
                            ? () => onOpenWidget(meta)
                            : undefined;
                        // The catalog is shared, so a widget can name a full view only
                        // the OTHER shell has. Dropping the arrow silently is how
                        // "Renewals doesn't drill into anything" reads as the widget
                        // being broken rather than the target being elsewhere. Say it.
                        const openHint = hasTarget && !canOpen
                            ? (openHintFor ? openHintFor(meta) : 'Full view lives in the other app')
                            : undefined;
                        return (_jsxs("div", { className: "ab__cell", "data-rung": rung, children: [_jsx(WidgetShell, { title: meta ? meta.title : widget.type, onOpen: open, editing: editing, chromeless: meta ? meta.chromeless : false, onRemove: () => removeWidget(widget.id), onConfigure: Editor
                                        ? () => setConfiguring((c) => (c === widget.id ? null : widget.id))
                                        : undefined, onKeyMove: (dx, dy, dw, dh) => keyMove(widget.id, dx, dy, dw, dh), fill: meta ? meta.fill : false, openHint: openHint, children: Body ? (_jsx(Body, { config: widget.config, editing: editing, onOpen: open, fill: meta ? meta.fill : false, size: rung })) : (
                                    // A board arranged in the other shell (or a newer build)
                                    // can name a widget this one doesn't implement. Say so
                                    // and keep its slot — never drop it, or switching shells
                                    // would quietly delete widgets.
                                    _jsx("div", { className: "abw__status abw__status--empty", children: meta
                                            ? `"${meta.title}" isn't available in this app.`
                                            : 'This widget isn’t available in this app.' })) }), editing && configuring === widget.id && Editor && (_jsx("div", { className: "ab__cell-config", children: _jsx(Editor, { config: widget.config, onChange: (cfg) => updateWidgetConfig(widget.id, cfg), onClose: () => setConfiguring(null) }) }))] }, widget.id));
                    }) })) }))] }));
}
function SaveIndicator({ state, onRetry }) {
    if (state === 'idle')
        return null;
    if (state === 'saving')
        return _jsx("span", { className: "ab__save", children: "Saving\u2026" });
    if (state === 'saved')
        return _jsx("span", { className: "ab__save ab__save--ok", children: "Saved" });
    return (_jsxs("span", { className: "ab__save ab__save--err", children: ["Couldn\u2019t save", onRetry && (_jsx("button", { type: "button", className: "ab__btn ab__btn--quiet", onClick: onRetry, children: "Retry" }))] }));
}
export { ACCOUNT_WIDGET_META, normalizeAccountDashboard };
